import { useContext, useMemo } from 'react'

import { type SanityCombinedListingProduct } from '@data/sanity/queries/types/product'
import { type AttributeInput } from '@data/shopify/storefront/types'
import { type CartFormValues } from '@lib/cart/types'
import { ProductContext } from '@lib/product/context'
import { StringsContext } from '@lib/strings-context'

// Namespaces
export const shopifyCustomNamespace = 'custom'
export const shopifyGlobalNamespace = 'global'
export const shopifySanityNamespace = 'sanity'

// Metafield keys
export const shopifyCombinedListingDiscountMetafield =
  'combined_listing_discount'
export const shopifyCustomerSyncMetafield = 'customer_sync'
export const shopifyDescriptionTagMetafield = 'description_tag'
export const shopifyProductIdMetafield = 'product_id'
export const shopifyProductSyncMetafield = 'product_sync'
export const shopifyTitleTagMetafield = 'title_tag'
export const shopifyVatNumberMetafield = 'vat_number'

// Attribute names
export const shopifyCombinedListingIdAttribute = '_combined_listing_id'
export const shopifyCombinedListingPassepartoutHoleSizeAttribute =
  '_combined_listing_passepartout_cut_out_size'
export const shopifyCombinedListingProductIdsAttribute =
  '_combined_listing_product_ids'
export const shopifyCombinedListingProductUrlAttribute =
  '_combined_listing_product_url'
export const shopifyFrameBuilderTypeAttribute = '_frame_builder_type'
export const shopifyProductPassepartoutHoleSizeAttribute =
  '_product_passepartout_cut_out_size'
export const shopifyTaxExemptAttribute = '_tax_exempt'
export const shopifyTaxVatIdAttribute = '_vat_id'

// Attribute values
export const shopifyYesAttributeValue = 'yes'
export const shopifyNoAttributeValue = 'no'

/**
 * Gets tax-related cart attributes.
 */
export const getTaxCartAttributes = (
  cartFormValues: CartFormValues,
  taxExempt: boolean,
) => {
  const attributes: AttributeInput[] = []

  if (cartFormValues.vatId) {
    // Set VAT ID checkout attribute
    attributes.push({
      key: shopifyTaxVatIdAttribute,
      value: cartFormValues.vatId,
    })

    // Set tax exemption checkout attribute
    attributes.push({
      key: shopifyTaxExemptAttribute,
      value: taxExempt ? shopifyYesAttributeValue : shopifyNoAttributeValue,
    })
  }

  return attributes
}

/**
 * Gets hook that returns product cart attributes.
 */
export const useProductCartAttributes = (
  product?: SanityCombinedListingProduct,
) => {
  const { passepartoutHoleSize } = useContext(ProductContext)
  const strings = useContext(StringsContext)

  return useMemo(() => {
    const attributes: AttributeInput[] = []

    // Passepartout cut-out size attribute
    if (
      product &&
      product.combinedListingProductType === 'passepartout' &&
      passepartoutHoleSize
    ) {
      attributes.push({
        key: shopifyProductPassepartoutHoleSizeAttribute,
        value: passepartoutHoleSize,
      })
      attributes.push({
        key: strings.productPassepartoutHoleSize,
        value: passepartoutHoleSize,
      })
    }

    return attributes
  }, [passepartoutHoleSize, product, strings])
}
